import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import { Button, Input, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";

import {
  AppTable,
  ColumnHeader,
  DateColumn,
  TextColumn,
} from "../../components";
import { LiveLectureInvitationDto } from "../../interfaces";
import {
  useCreateLiveLectureInvitationMutation,
  useGetLiveLectureInvitationsQuery,
} from "../../store/api/liveLectureApi";

type TProps = {
  liveLectureId: string;
};

export const LiveLectureInvitationsPanel: React.FC<TProps> = ({
  liveLectureId,
}) => {
  const { t } = useTranslation();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const {
    data: invitations = [],
    isLoading,
    isFetching,
    refetch,
  } = useGetLiveLectureInvitationsQuery(liveLectureId);
  const [createInvitation, { isLoading: isCreating }] =
    useCreateLiveLectureInvitationMutation();

  const onCreateInvite = async () => {
    try {
      const invitation = await createInvitation(liveLectureId).unwrap();
      if (invitation.inviteUrl && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(invitation.inviteUrl);
        setCopiedId(invitation.id);
        toast.success(t("inviteLinkCopied"));
        window.setTimeout(() => setCopiedId(null), 2000);
      }
    } catch {
      toast.error(t("error"));
    }
  };

  const onCopyLink = async (id: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      toast.success(t("inviteLinkCopied"));
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error(t("error"));
    }
  };

  const columns: TableColumn<LiveLectureInvitationDto>[] = useMemo(
    () => [
      {
        name: <ColumnHeader title="inviteUrl" />,
        cell: (row) => {
          const isCopied = copiedId === row.id;
          return (
            <div className="input-group input-group-sm" style={{ minWidth: 220 }}>
              <Input
                type="text"
                value={row.inviteUrl}
                readOnly
                className="form-control bg-light"
                onFocus={(e) => e.target.select()}
                title={row.inviteUrl}
              />
              <button
                type="button"
                className={`btn btn-sm ${
                  isCopied ? "btn-success" : "btn-soft-primary"
                }`}
                onClick={() => onCopyLink(row.id, row.inviteUrl)}
                title={t("copyInviteLink")}
              >
                <i
                  className={
                    isCopied ? "ri-check-line" : "ri-file-copy-line"
                  }
                ></i>
                <span className="ms-1 d-none d-xl-inline">
                  {isCopied ? t("copied") : t("copyInviteLink")}
                </span>
              </button>
            </div>
          );
        },
        grow: 3,
        minWidth: "260px",
      },
      {
        name: <ColumnHeader title="guestName" />,
        cell: (row) => (
          <TextColumn text={row.guestName || "-"} isNeedToTranslate={false} />
        ),
        minWidth: "100px",
      },
      {
        name: <ColumnHeader title="firstJoinedAt" />,
        cell: (row) =>
          row.firstJoinedAt ? (
            <DateColumn date={row.firstJoinedAt} />
          ) : (
            <TextColumn text="-" isNeedToTranslate={false} />
          ),
        minWidth: "120px",
      },
      {
        name: <ColumnHeader title="invitedAt" />,
        cell: (row) => (
          <TextColumn
            text={moment(row.createdAt).format("lll")}
            isNeedToTranslate={false}
          />
        ),
        minWidth: "140px",
      },
    ],
    [t, copiedId],
  );

  return (
    <div className="mb-3 border rounded p-3">
      <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
        <div>
          <h6 className="mb-0">{t("liveLectureInvitations")}</h6>
          <p className="text-muted small mb-0 mt-1">
            {t("privateLiveLectureHint")}
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            color="light"
            size="sm"
            className="btn-soft-secondary"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? (
              <Spinner size="sm" />
            ) : (
              <>
                <i className="ri-refresh-line me-1"></i>
                {t("refresh")}
              </>
            )}
          </Button>
          <Button
            color="success"
            size="sm"
            onClick={onCreateInvite}
            disabled={isCreating}
          >
            {isCreating ? (
              <Spinner size="sm" className="me-1" />
            ) : (
              <i className="ri-link me-1"></i>
            )}
            {t("createInviteLink")}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-3">
          <Spinner size="sm" />
        </div>
      ) : invitations.length ? (
        <AppTable
          columns={columns}
          data={invitations}
          pagination={false}
          progressPending={isFetching}
        />
      ) : (
        <div className="text-center text-muted py-3">{t("noInvitationsYet")}</div>
      )}
    </div>
  );
};
